// projectlab-web/src/components/auth/ProtectedRoute.tsx
import { useEffect, useRef, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { PageLoader } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { createLogger } from '@/lib/logger'

const log = createLogger('ProtectedRoute')

interface ProtectedRouteProps {
  allowedRoles: string[]
  isOnboarding?: boolean
}

/**
 * How many times to re-check for a profile before giving up and
 * redirecting to onboarding. Each attempt waits RETRY_DELAY_MS.
 */
const MAX_RETRIES = 4
const RETRY_DELAY_MS = 600

export function ProtectedRoute({ allowedRoles, isOnboarding = false }: ProtectedRouteProps) {
  const { user, role, dbProfile, loading, refreshProfile } = useAuth()
  const location = useLocation()
  const retryCount = useRef(0)
  const [retrying, setRetrying] = useState(false)
  const [readyToRedirect, setReadyToRedirect] = useState(false)

  useEffect(() => {
    // Only run this when auth is settled, user exists, role matches, but profile is missing
    if (loading) return
    if (!user) return
    if (!allowedRoles.includes(role ?? '')) return
    if (dbProfile) {
      retryCount.current = 0
      setReadyToRedirect(false)
      return
    }
    if (isOnboarding) return // onboarding routes don't need a profile row yet

    // Profile is missing — retry a few times before concluding onboarding is needed
    if (retryCount.current >= MAX_RETRIES) {
      log.info('No profile row — redirecting to onboarding', { role, uid: user.id })
      setReadyToRedirect(true)
      return
    }

    setRetrying(true)
    const timer = window.setTimeout(async () => {
      retryCount.current += 1
      try {
        await refreshProfile()
      } catch (e) {
        log.warn('refreshProfile retry failed', e)
      }
      setRetrying(false)
    }, RETRY_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [loading, user, role, dbProfile, isOnboarding, allowedRoles, refreshProfile])

  if (loading || retrying) return <PageLoader />

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (!allowedRoles.includes(role ?? '')) {
    return <Navigate to="/login" replace />
  }

  if (!isOnboarding && !dbProfile && readyToRedirect) {
    const onboardingPath = role === 'owner' ? '/owner/onboarding' : '/talent/onboarding'
    return <Navigate to={onboardingPath} replace />
  }

  return <Outlet />
}
